import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAllPosts } from '../utils/blogLoader';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import SEO from '../components/SEO';
import {
  TagIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CommandLineIcon,
  UserIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import PageHeader from '../components/PageHeader';
import SchemaMarkup from '../components/SchemaMarkup';

// --- HELPER: Format Date to "14 Feb 2026" ---
const formatDate = (dateStr, lang = 'en') => {
  if (!dateStr) return '';
  const locale = lang === 'et' ? 'et-EE' : 'en-GB';
  return new Date(dateStr).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
};

const PostImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="mb-12 rounded-2xl overflow-hidden border border-slate-100 bg-slate-100 h-64 flex items-center justify-center">
        <div className="absolute inset-0 opacity-10 rounded-2xl" style={{ backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)', backgroundSize: '16px 16px' }}></div>
        <CommandLineIcon className="w-12 h-12 text-slate-300" />
      </div>
    );
  }

  return (
    <div className="mb-12 rounded-2xl overflow-hidden shadow-sm border border-slate-100 bg-slate-50">
      <img
        src={src}
        alt={alt}
        width="1200"
        height="600"
        fetchpriority="high"
        decoding="async"
        className="w-full h-auto object-cover max-h-[600px]"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

export default function BlogPost() {
  const { slug, lang } = useParams();
  const { t } = useTranslation();
  const currentLang = lang || 'et';
  
  const posts = getAllPosts();
  const post = posts.find((p) => p.slug === slug);

  // Social scrapers can't render SVG og:images, so use the raster .jpg twin
  // (generated by scripts/blog-og-images.mjs). The on-page hero still uses the SVG.
  const ogImage = post?.image ? post.image.replace(/\.svg$/, '.jpg') : post?.image;

  // Bilingual blog: if this post has a translation (altSlug), expose hreflang pairs.
  const otherLang = post?.lang === 'et' ? 'en' : 'et';
  const alternates = post?.altSlug
    ? [
        { hreflang: post.lang, href: `/${post.lang}/blog/${post.slug}` },
        { hreflang: otherLang, href: `/${otherLang}/blog/${post.altSlug}` },
      ]
    : undefined;

  const relatedPosts = posts
    .filter(p => p.slug !== slug && p.lang === post?.lang)
    .sort((a, b) => {
       const aHasTag = post?.tags?.[0] && a.tags?.includes(post.tags[0]);
       const bHasTag = post?.tags?.[0] && b.tags?.includes(post.tags[0]);
       if (aHasTag && !bHasTag) return -1;
       if (!aHasTag && bHasTag) return 1;
       return 0;
    })
    .slice(0, 2);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center flex-col font-mono">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">404</h1>
        <p className="text-slate-600 mb-8">{t('blog_page.read_post')}</p>
        <Link to={`/${currentLang}/blog`} className="px-6 py-3 bg-slate-900 text-white rounded-full font-bold">{t('blog_page.back_to_blog')}</Link>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <SEO title={post.title} description={post.description} url={`/${post.lang}/blog/${post.slug}`} image={ogImage} date={post.date} updated={post.updated} alternates={alternates} />

      <SchemaMarkup
        type="article"
        data={{
          title: post.title,
          description: post.description,
          image: ogImage,
          date: post.date,
          updated: post.updated,
          tags: post.tags,
          url: `/${currentLang}/blog/${post.slug}`
        }}
      />

      <PageHeader 
        title={post.title} 
        subtitle={post.description} 
        maxWidth="max-w-6xl"
      />

      <div className="container mx-auto px-4 py-12 md:py-16 max-w-6xl">
        
        {/* Back Link */}
        <Link
          to={`/${currentLang}/blog`}
          className="group inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-12 font-bold text-xs uppercase tracking-widest"
        >
           <ArrowLeftIcon className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
           {t('blog_page.back_to_blog')}
        </Link>
        
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8 md:p-16 mb-16 relative">
            
            {/* Metadata Bar */}
            <div className="flex flex-wrap items-center gap-6 text-sm font-mono text-slate-500 mb-10 pb-8 border-b border-slate-100">
                <div className="flex items-center gap-2 text-blue-700 font-bold uppercase tracking-wider">
                    <span className="w-2 h-2 rounded-full bg-blue-600 motion-safe:animate-pulse"></span>
                    {`${t('blog_page.published')} · ${formatDate(post.date, post.lang)}`}
                </div>
                {post.readingTime && (
                  <span className="text-slate-500 font-mono text-xs">{post.readingTime} {t('blog_page.min_read')}</span>
                )}
                {post.updated && (
                  <span className="flex items-center gap-2 text-emerald-700 font-bold uppercase text-xs tracking-wider">
                    <ArrowPathIcon className="w-3.5 h-3.5" /> {t('blog_page.updated')} {formatDate(post.updated, post.lang)}
                  </span>
                )}
                <span className="flex items-center gap-2 text-slate-600 font-bold uppercase text-xs tracking-wider">
                  <UserIcon className="w-3.5 h-3.5 text-blue-600" /> Jaanek Liiskmaa
                </span>
                {post.tags && post.tags.length > 0 && (
                  <div className="flex items-center gap-3">
                     <span className="text-slate-300">/</span>
                     {post.tags.map(tag => (
                        <span key={tag} className="flex items-center gap-1 text-slate-600 font-bold uppercase text-xs">
                           <TagIcon className="w-3 h-3" /> {tag}
                        </span>
                     ))}
                  </div>
                )}
            </div>

            {post.image && <PostImage src={post.image} alt={post.title} />}

            <article className="prose prose-lg prose-slate max-w-4xl mx-auto
                prose-headings:font-bold prose-headings:text-slate-900 prose-headings:font-sans
                prose-p:text-slate-600 prose-p:leading-relaxed
                prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-slate-900
                prose-blockquote:border-l-4 prose-blockquote:border-blue-600 prose-blockquote:bg-slate-50 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:not-italic prose-blockquote:rounded-r-lg
                prose-code:text-blue-700 prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:font-mono prose-code:text-sm prose-code:before:content-none prose-code:after:content-none
                prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-pre:shadow-xl prose-pre:rounded-xl
                [&_pre>code]:bg-transparent [&_pre>code]:p-0 [&_pre>code]:text-slate-100 [&_pre>code]:rounded-none [&_pre>code]:text-sm
                prose-li:marker:text-blue-600
                prose-img:rounded-xl prose-img:shadow-lg
            ">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {post.content}
                </ReactMarkdown>
            </article>
            
        </div>

        {relatedPosts.length > 0 && (
        <div className="mb-24">
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-8 flex items-center gap-2">
                <span className="w-8 h-[1px] bg-slate-300"></span> {t('blog_page.related_title')}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
            {relatedPosts.map((related) => (
                <Link 
                key={related.slug} 
                to={`/${currentLang}/blog/${related.slug}`}
                className="group bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-400 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                >
                <div className="text-xs font-mono font-bold text-blue-600 mb-3 uppercase tracking-wider flex items-center gap-2">
                     <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                    {related.tags?.[0] || 'ARTICLE'}
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-tight">
                    {related.title}
                </h4>
                <div className="mt-auto pt-4 flex items-center text-sm font-bold text-slate-900 group-hover:gap-2 transition-all">
                    {t('blog_page.read_post')} <ArrowRightIcon className="w-4 h-4 ml-2 text-blue-600" />
                </div>
                </Link>
            ))}
            </div>
        </div>
        )}
        
        {/* CTA */}
        <div className="bg-slate-900 rounded-3xl p-8 md:p-12 text-center text-white shadow-2xl relative overflow-hidden border border-slate-800">
            <div className="absolute inset-0 opacity-20 pointer-events-none" 
                  style={{ backgroundImage: 'radial-gradient(#22d3ee 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
            </div>
            
            <div className="relative z-10 max-w-2xl mx-auto">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">{t('blog_page.cta_title')}</h3>
                <p className="text-slate-400 mb-10 text-lg">{t('blog_page.cta_desc')}</p>
                <Link to={`/${currentLang}/contact`} className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full font-bold hover:bg-blue-400 hover:scale-105 transition-all shadow-lg">
                    {t('blog_page.cta_btn')} <ArrowRightIcon className="w-4 h-4" />
                </Link>
            </div>
         </div>
      </div>
    </div>
  );
}
